import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  GoogleAuthProvider,
  OAuthProvider,
  User as FirebaseUser,
  UserCredential,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../firebase/config";
import { analyticsService } from "../services/analyticsService";
import { userService } from "../services/userService";
import type { AuthState, DeletionRequestStatus, UserData } from "../types/user";

export interface PendingTwoFactor {
  uid: string;
  availableMethods: Array<"email" | "authenticator">;
  method: "email" | "authenticator" | null;
}

export interface ProviderSignInResult {
  user: FirebaseUser;
  isNewUser: boolean;
  firstName: string;
}

export const describeProviderResult = (result: UserCredential): ProviderSignInResult => {
  const info = getAdditionalUserInfo(result);
  const profile = (info?.profile ?? {}) as {
    given_name?: string;
    name?: { firstName?: string } | string;
  };
  const nested = typeof profile.name === "object" ? profile.name?.firstName : undefined;
  const firstName =
    profile.given_name || nested || (result.user.displayName ?? "").split(" ")[0] || "";
  return { user: result.user, isNewUser: Boolean(info?.isNewUser), firstName };
};

function deletionMessage(status: DeletionRequestStatus | undefined): string | null {
  if (status === "pending") {
    return "We sent you a confirmation email for your account deletion request. Please confirm it, or contact support to cancel.";
  }
  if (status === "confirmed") {
    return "Your account deletion request is confirmed and scheduled. We'll email you when it's ready to complete.";
  }
  return null;
}

interface AuthContextValue {
  currentUser: FirebaseUser | null;
  userData: UserData | null;
  authState: AuthState;
  initializing: boolean;
  pendingTwoFactor: PendingTwoFactor | null;
  createAccountWithEmail: (email: string, password: string) => Promise<FirebaseUser>;
  login: (email: string, password: string) => Promise<void>;
  continueWithGoogle: () => Promise<UserCredential | undefined>;
  continueWithApple: () => Promise<ProviderSignInResult | undefined>;
  sendPasswordReset: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  selectTwoFactorMethod: (method: "email" | "authenticator") => void;
  completeTwoFactorLogin: (code: string, method: "email" | "authenticator") => Promise<void>;
  cancelTwoFactorLogin: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [initializing, setInitializing] = useState(true);
  const [pendingTwoFactor, setPendingTwoFactor] = useState<PendingTwoFactor | null>(null);
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    loading: true,
    error: null,
  });
  const pendingTwoFactorRef = useRef<PendingTwoFactor | null>(null);
  pendingTwoFactorRef.current = pendingTwoFactor;
  const deletionBlockMessageRef = useRef<string | null>(null);

  const readTwoFactorStatus = async (uid: string): Promise<PendingTwoFactor | null> => {
    try {
      const snap = await getDoc(doc(db, "users", uid));
      if (!snap.exists() || !snap.data()?.twoFactorEnabled) return null;
      const data = snap.data();
      const methods: Array<"email" | "authenticator"> = [];
      if (data.twoFactorMethods?.email) methods.push("email");
      if (data.twoFactorMethods?.authenticator) methods.push("authenticator");
      if (methods.length === 0 && data.twoFactorMethod) {
        methods.push(data.twoFactorMethod);
      }
      if (methods.length === 0) return null;
      return {
        uid,
        availableMethods: methods,
        method: methods.length === 1 ? methods[0] : null,
      };
    } catch {
      return null;
    }
  };

  const refreshUserData = async () => {
    if (!auth.currentUser) return;
    const data = await userService.getUserData(auth.currentUser.uid);
    setUserData(data);
    setAuthState({
      isAuthenticated: true,
      user: data,
      loading: false,
      error: data ? null : "User data not found",
    });
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (pendingTwoFactorRef.current) {
        setInitializing(false);
        return;
      }
      if (!user) {
        setUserData(null);
        const blocked = deletionBlockMessageRef.current;
        deletionBlockMessageRef.current = null;
        setAuthState({ isAuthenticated: false, user: null, loading: false, error: blocked });
        setInitializing(false);
        return;
      }

      setAuthState((prev) => ({ ...prev, loading: true }));
      try {
        const data = await userService.getUserData(user.uid);
        const blocked = deletionMessage(data?.deletionStatus);
        if (blocked) {
          deletionBlockMessageRef.current = blocked;
          await signOut(auth);
          return;
        }
        setUserData(data);
        setAuthState({
          isAuthenticated: true,
          user: data,
          loading: false,
          error: data ? null : "User data not found",
        });
      } catch (error) {
        setUserData(null);
        setAuthState({
          isAuthenticated: false,
          user: null,
          loading: false,
          error: error instanceof Error ? error.message : "Could not load your profile.",
        });
      } finally {
        setInitializing(false);
      }
    });
    return unsubscribe;
  }, []);

  const createAccountWithEmail = async (email: string, password: string) => {
    setAuthState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      analyticsService.trackAuth("signup", "email");
      return credential.user;
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Could not create the account.",
      }));
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    setAuthState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const twoFa = await readTwoFactorStatus(credential.user.uid);
      if (twoFa) {
        pendingTwoFactorRef.current = twoFa;
        setPendingTwoFactor(twoFa);
        setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
        return;
      }
      analyticsService.trackAuth("login", "email");
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Could not sign in.",
      }));
      throw error;
    }
  };

  const continueWithProvider = async (provider: GoogleAuthProvider | OAuthProvider) => {
    setAuthState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const result = await signInWithPopup(auth, provider);
      const twoFa = await readTwoFactorStatus(result.user.uid);
      if (twoFa) {
        pendingTwoFactorRef.current = twoFa;
        setPendingTwoFactor(twoFa);
        setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
        return undefined;
      }
      return result;
    } catch (error) {
      setAuthState((prev) => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : "Could not sign in.",
      }));
      throw error;
    }
  };

  const continueWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    provider.addScope("email");
    provider.addScope("profile");
    const result = await continueWithProvider(provider);
    if (result) analyticsService.trackAuth("login", "google");
    return result;
  };

  const continueWithApple = async () => {
    const provider = new OAuthProvider("apple.com");
    provider.addScope("email");
    provider.addScope("name");
    const result = await continueWithProvider(provider);
    if (!result) return undefined;
    analyticsService.trackAuth("login", "apple");
    return describeProviderResult(result);
  };

  const sendPasswordReset = async (email: string) => {
    await sendPasswordResetEmail(auth, email.trim());
  };

  const logout = async () => {
    pendingTwoFactorRef.current = null;
    setPendingTwoFactor(null);
    await signOut(auth);
    analyticsService.trackAuth("logout");
  };

  const selectTwoFactorMethod = (method: "email" | "authenticator") => {
    setPendingTwoFactor((current) => {
      if (!current) return current;
      const updated = { ...current, method };
      pendingTwoFactorRef.current = updated;
      return updated;
    });
  };

  const completeTwoFactorLogin = async (code: string, method: "email" | "authenticator") => {
    if (!pendingTwoFactor) throw new Error("No pending verification.");
    const verifyFn = httpsCallable(functions, "verifyTwoFactorLogin");
    const result = await verifyFn({ code, method, uid: pendingTwoFactor.uid });
    const customToken = (result.data as { customToken?: string }).customToken;
    if (!customToken) throw new Error("Verification did not return a session.");
    pendingTwoFactorRef.current = null;
    setPendingTwoFactor(null);
    await signInWithCustomToken(auth, customToken);
  };

  const cancelTwoFactorLogin = () => {
    pendingTwoFactorRef.current = null;
    setPendingTwoFactor(null);
    signOut(auth).catch(() => {});
    setAuthState({ isAuthenticated: false, user: null, loading: false, error: null });
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        userData,
        authState,
        initializing,
        pendingTwoFactor,
        createAccountWithEmail,
        login,
        continueWithGoogle,
        continueWithApple,
        sendPasswordReset,
        logout,
        refreshUserData,
        selectTwoFactorMethod,
        completeTwoFactorLogin,
        cancelTwoFactorLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
}
